<template>
  <div class="bolt-sync-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Bolt Sync</h1>
        <div class="page-subtitle">Kierowcy i pojazdy z floty Bolta</div>
      </div>
      <div class="d-flex align-center">
      <v-btn
        v-if="activeTab === 0"
        outlined color="info" @click="loadPreview" :loading="loading"
      >
        <v-icon left>mdi-refresh</v-icon>{{ $t('boltSync.loadPreview') }}
      </v-btn>
      <v-btn
        v-else
        outlined color="info" @click="loadVehiclesPreview" :loading="vehLoading"
      >
        <v-icon left>mdi-refresh</v-icon>Odśwież listę pojazdów
      </v-btn>
      </div>
    </div>

    <v-tabs
      v-model="activeTab"
      background-color="transparent"
      class="mb-4 bolt-sync-tabs"
    >
      <v-tab v-if="canAccess('drivers')">
        <v-icon small left>mdi-account-multiple</v-icon>
        Kierowcy
      </v-tab>
      <v-tab v-if="canAccess('vehicles')">
        <v-icon small left>mdi-car-multiple</v-icon>
        Pojazdy
        <span
          v-if="vehPreview && vehPreview.autoAssign && vehPreview.autoAssign.length"
          class="bolt-sync-tab-count"
        >
          {{ vehPreview.autoAssign.length }}
        </span>
        <span
          v-if="vehPreview && vehPreview.suspended && vehPreview.suspended.length"
          class="bolt-sync-tab-count bolt-sync-tab-count--warn"
        >
          {{ vehPreview.suspended.length }}
        </span>
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" style="background: transparent;" touchless>
    <v-tab-item v-if="canAccess('drivers')">

    <!-- Wskaźnik świeżości danych z BoltFetcher (cron co godzinę) -->
    <v-alert v-if="fetcherStatus" :type="fetcherFreshnessType" outlined dense class="mb-3">
      <div class="d-flex align-center">
        <v-icon left :color="fetcherFreshnessColor">{{ fetcherFreshnessIcon }}</v-icon>
        <div class="flex-grow-1">
          <div class="font-weight-bold">{{ fetcherFreshnessLabel }}</div>
          <div class="caption">
            <span v-if="fetcherStatus.lastDone">
              Ostatnio: {{ formatRelative(fetcherStatus.lastDone.finishedAt) }}
              ({{ fetcherStatus.lastDone.ordersUpserted || 0 }} zam, {{ fetcherStatus.lastDone.driversUpserted || 0 }} kierowców).
            </span>
            <span v-else>Nigdy. Kliknij „Synchronizuj teraz".</span>
            Cron: co godzinę o 7. minucie.
          </div>
        </div>
        <v-btn x-small color="primary" :loading="fetcherRunning" @click="runFetcher">
          <v-icon x-small left>mdi-cloud-sync</v-icon>Synchronizuj teraz
        </v-btn>
      </div>
    </v-alert>

    <v-alert v-if="error" type="error" outlined class="mb-3">{{ error }}</v-alert>

    <!-- Status background-joba — pokazuje wiadomość z progress co 2.5s.
         Synchronizacja chodzi w tle, można zostawić zakładkę otwartą lub przeglądać inne strony. -->
    <v-alert v-if="loading || applying" type="info" outlined class="mb-3" dense>
      <div class="d-flex align-center">
        <v-progress-circular indeterminate size="18" width="2" class="mr-3" />
        <div>
          <div class="font-weight-bold">{{ applying ? 'Stosuję zmiany w tle…' : 'Synchronizacja w tle…' }}</div>
          <div class="caption">{{ jobMessage || 'Komunikuję się z API Bolta. Operacja może trwać kilka minut — możesz przejść na inną zakładkę.' }}</div>
        </div>
      </div>
    </v-alert>

    <!-- Wynik ostatniego apply — pokazuje sukces + błędy pogrupowane po przyczynie. -->
    <v-card v-if="lastResult" outlined class="mb-3 rounded-lg">
      <v-card-title class="py-2">
        <v-icon left :color="lastErrors.length ? 'warning' : 'success'">
          {{ lastErrors.length ? 'mdi-alert' : 'mdi-check-circle' }}
        </v-icon>
        Wynik ostatniego apply
        <v-spacer />
        <v-chip x-small color="success" outlined class="mr-1">Przypisano: {{ lastResult.assigned || 0 }}</v-chip>
        <v-chip x-small color="primary" outlined class="mr-1" v-if="lastResult.created">Utworzono: {{ lastResult.created }}</v-chip>
        <v-chip x-small color="info" outlined class="mr-1" v-if="lastResult.conflictsResolved">Konflikty: {{ lastResult.conflictsResolved }}</v-chip>
        <v-chip x-small color="error" outlined v-if="lastErrors.length">Błędy: {{ lastErrors.length }}</v-chip>
        <v-btn icon x-small class="ml-2" @click="lastResult = null; lastErrors = []">
          <v-icon x-small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider v-if="errorGroups.length" />
      <v-card-text v-if="errorGroups.length" class="pa-0">
        <v-expansion-panels accordion flat>
          <v-expansion-panel v-for="g in errorGroups" :key="g.key">
            <v-expansion-panel-header class="py-2">
              <div class="d-flex align-center">
                <v-icon small color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
                <strong>{{ g.label }}</strong>
                <v-chip x-small class="ml-2" color="error" outlined>{{ g.count }}</v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th style="width: 220px;">Kierowca z Bolta</th>
                    <th>Powód</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(e, i) in g.items" :key="i">
                    <td>
                      <div v-if="e.context && (e.context.first_name || e.context.last_name)">
                        <strong>{{ e.context.first_name }} {{ e.context.last_name }}</strong>
                      </div>
                      <div v-else-if="e.context && e.context.ourDriver">
                        <strong>{{ e.context.ourDriver.firstName }} {{ e.context.ourDriver.lastName }}</strong>
                      </div>
                      <div v-else class="caption grey--text">—</div>
                      <div v-if="e.context && e.context.email" class="caption grey--text">{{ e.context.email }}</div>
                      <div v-if="e.context && e.context.phone" class="caption grey--text">{{ e.context.phone }}</div>
                    </td>
                    <td><span class="caption">{{ e.reason }}</span></td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>

    <div v-if="preview">
      <!-- Summary chips -->
      <v-card outlined class="mb-4" style="background: var(--bg-surface);">
        <v-card-text class="d-flex flex-wrap" style="gap: 8px;">
          <v-chip color="info" outlined>{{ $t('boltSync.totalBolt') }}: {{ preview.totalBoltDrivers }}</v-chip>
          <v-chip color="success" outlined>{{ $t('boltSync.alreadyLinked') }}: {{ preview.alreadyLinkedCount }}</v-chip>
          <v-chip color="primary" outlined>Auto: {{ preview.autoAssign.length }}</v-chip>
          <v-chip color="warning" outlined>Sugerowane: {{ preview.suggestions.length }}</v-chip>
          <v-chip color="error" outlined v-if="preview.idConflicts.length">Konflikt ID: {{ preview.idConflicts.length }}</v-chip>
          <v-chip color="purple" outlined v-if="preview.toCreate.length">Do utworzenia: {{ preview.toCreate.length }}</v-chip>
          <v-chip color="grey" outlined v-if="(preview.ourUnmatched || []).length">Bez dopasowania: {{ (preview.ourUnmatched || []).length }}</v-chip>
          <v-chip
            v-if="preview.ignoredCount"
            color="grey darken-2"
            outlined
            class="white--text"
            @click="showIgnoredDialog = true"
            style="cursor: pointer;"
          >
            <v-icon left small>mdi-eye-off</v-icon>
            Zignorowani: {{ preview.ignoredCount }}
          </v-chip>
        </v-card-text>
      </v-card>

      <!-- Diagnostyka: dlaczego nieprzypisani — sumaryczna lista po Twoich kierowcach -->
      <v-card outlined class="mb-4" style="background: var(--bg-surface); border-color: rgba(255,255,255,0.06);">
        <v-card-title class="subtitle-1">
          <v-icon left color="info">mdi-account-question</v-icon>
          Twoi kierowcy BTP — status linkowania ({{ unmatchedSummary.length }})
        </v-card-title>
        <v-card-subtitle class="grey--text">
          Tylko kierowcy którzy nadal NIE mają zapisanego Bolt UUID. Powód mówi czy da się ich
          przypisać w tej zakładce, czy nie ma w ogóle dopasowania w Bolcie.
        </v-card-subtitle>
        <v-card-text v-if="unmatchedSummary.length === 0" class="success--text">
          Wszyscy Twoi aktywni kierowcy są zlinkowani z Bolta. Nic do zrobienia tutaj.
        </v-card-text>
        <v-data-table
          v-else
          :items="unmatchedSummary"
          :headers="unmatchedHeaders"
          :items-per-page="25"
          :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
          dark dense
        >
          <template v-slot:[`item.driver`]="{ item }">
            <div class="font-weight-medium">{{ item.firstName }} {{ item.lastName }}</div>
            <div class="caption grey--text">{{ item.email || '—' }} · {{ item.phone || '—' }}</div>
          </template>
          <template v-slot:[`item.reason`]="{ item }">
            <v-chip x-small :color="reasonColor(item.reasonKey)" outlined>{{ item.reason }}</v-chip>
          </template>
          <template v-slot:[`item.action`]="{ item }">
            <v-btn x-small text color="primary" v-if="item.reasonKey !== 'no-bolt-match'"
                   @click="scrollToSection(item.reasonKey)">
              Idź do sekcji ↓
            </v-btn>
            <span v-else class="caption grey--text">Brak akcji</span>
          </template>
        </v-data-table>
      </v-card>

      <!-- AUTO-MATCHES -->
      <v-card outlined class="mb-4" v-if="preview.autoAssign.length" style="background: var(--bg-surface);">
        <v-card-title class="subtitle-1">
          <v-icon left color="success">mdi-check-circle</v-icon>
          {{ $t('boltSync.autoAssignTitle') }} ({{ preview.autoAssign.length }})
        </v-card-title>
        <v-card-subtitle class="grey--text">{{ $t('boltSync.autoAssignHint') }}</v-card-subtitle>
        <v-card-text class="py-0">
          <v-btn x-small text @click="selectAll('auto', true)">{{ $t('boltSync.acceptAll') }}</v-btn>
          <v-btn x-small text @click="selectAll('auto', false)">{{ $t('boltSync.rejectAll') }}</v-btn>
        </v-card-text>
        <v-data-table
          :items="preview.autoAssign"
          :headers="matchHeaders"
          :items-per-page="-1"
          hide-default-footer
          dark dense
        >
          <template v-slot:[`item.select`]="{ item }">
            <v-checkbox v-model="selectedAuto[item.ourDriver.id]" hide-details dense />
          </template>
          <template v-slot:[`item.ourDriver`]="{ item }">
            <div class="font-weight-medium">{{ item.ourDriver.firstName }} {{ item.ourDriver.lastName }}</div>
            <div class="caption grey--text">{{ item.ourDriver.email }} · {{ item.ourDriver.phone }}</div>
          </template>
          <template v-slot:[`item.boltDriver`]="{ item }">
            <div class="font-weight-medium">{{ item.boltDriver.first_name }} {{ item.boltDriver.last_name }}</div>
            <div class="caption grey--text">{{ item.boltDriver.email }} · {{ item.boltDriver.phone }}</div>
            <code class="caption">{{ item.boltDriver.driver_uuid.slice(0, 8) }}...</code>
          </template>
          <template v-slot:[`item.score`]="{ item }">
            <v-chip x-small :color="item.score >= 80 ? 'success' : 'warning'">{{ item.score }}</v-chip>
          </template>
          <template v-slot:[`item.reasons`]="{ item }">
            <v-chip v-for="r in item.reasons" :key="r" x-small outlined class="mr-1">{{ r }}</v-chip>
          </template>
        </v-data-table>
      </v-card>

      <!-- SUGGESTIONS -->
      <v-card outlined class="mb-4" v-if="preview.suggestions.length" style="background: var(--bg-surface);">
        <v-card-title class="subtitle-1">
          <v-icon left color="warning">mdi-help-circle</v-icon>
          {{ $t('boltSync.suggestionsTitle') }} ({{ preview.suggestions.length }})
        </v-card-title>
        <v-card-subtitle class="grey--text">{{ $t('boltSync.suggestionsHint') }}</v-card-subtitle>
        <v-data-table
          :items="preview.suggestions"
          :headers="matchHeaders"
          :items-per-page="-1"
          hide-default-footer
          dark dense
        >
          <template v-slot:[`item.select`]="{ item }">
            <v-checkbox v-model="selectedSuggestions[item.ourDriver.id]" hide-details dense />
          </template>
          <template v-slot:[`item.ourDriver`]="{ item }">
            <div class="font-weight-medium">{{ item.ourDriver.firstName }} {{ item.ourDriver.lastName }}</div>
            <div class="caption grey--text">{{ item.ourDriver.email }} · {{ item.ourDriver.phone }}</div>
          </template>
          <template v-slot:[`item.boltDriver`]="{ item }">
            <div class="font-weight-medium">{{ item.boltDriver.first_name }} {{ item.boltDriver.last_name }}</div>
            <div class="caption grey--text">{{ item.boltDriver.email }} · {{ item.boltDriver.phone }}</div>
            <code class="caption">{{ item.boltDriver.driver_uuid.slice(0, 8) }}...</code>
          </template>
          <template v-slot:[`item.score`]="{ item }">
            <v-chip x-small color="warning">{{ item.score }}</v-chip>
          </template>
          <template v-slot:[`item.reasons`]="{ item }">
            <v-chip v-for="r in item.reasons" :key="r" x-small outlined class="mr-1">{{ r }}</v-chip>
          </template>
        </v-data-table>
      </v-card>

      <!-- ID CONFLICTS -->
      <v-card outlined class="mb-4" v-if="preview.idConflicts.length" style="background: var(--bg-surface); border-color: #F44336;">
        <v-card-title class="subtitle-1 error--text">
          <v-icon left color="error">mdi-alert</v-icon>
          {{ $t('boltSync.idConflictsTitle') }} ({{ preview.idConflicts.length }})
        </v-card-title>
        <v-card-subtitle class="grey--text">{{ $t('boltSync.idConflictsHint') }}</v-card-subtitle>
        <v-card-text>
          <div v-for="m in preview.idConflicts" :key="m.ourDriver.id" class="mb-3 pa-2" style="border: 1px solid #F44336; border-radius: 4px;">
            <div class="font-weight-medium">{{ m.ourDriver.firstName }} {{ m.ourDriver.lastName }}</div>
            <div class="caption">
              {{ $t('boltSync.currentBoltId') }}: <code>{{ m.ourDriver.currentBoltId }}</code><br>
              {{ $t('boltSync.newBoltId') }}: <code class="warning--text">{{ m.boltDriver.driver_uuid }}</code>
            </div>
            <v-checkbox
              v-model="selectedConflicts[m.ourDriver.id]"
              label="Podmień ID"
              dense hide-details
              class="mt-2"
            />
          </div>
        </v-card-text>
      </v-card>

      <!-- TO CREATE -->
      <v-card outlined class="mb-4" v-if="preview.toCreate.length" style="background: var(--bg-surface);">
        <v-card-title class="subtitle-1">
          <v-icon left color="purple">mdi-account-plus</v-icon>
          {{ $t('boltSync.toCreateTitle') }} ({{ preview.toCreate.length }})
        </v-card-title>
        <v-card-subtitle class="grey--text">{{ $t('boltSync.toCreateHint') }}</v-card-subtitle>
        <v-card-text>
          <v-select
            v-model="defaultCoopType"
            :items="cooperationTypeOptions"
            :label="$t('boltSync.cooperationTypeDefault')"
            outlined dense hide-details
            class="mb-3"
            style="max-width: 300px;"
          />
          <v-btn x-small text @click="selectAll('create', true)">{{ $t('boltSync.acceptAll') }}</v-btn>
          <v-btn x-small text @click="selectAll('create', false)">{{ $t('boltSync.rejectAll') }}</v-btn>
        </v-card-text>
        <v-data-table
          :items="preview.toCreate"
          :headers="createHeaders"
          :items-per-page="-1"
          hide-default-footer
          dark dense
        >
          <template v-slot:[`item.select`]="{ item }">
            <v-checkbox v-model="selectedCreate[item.driver_uuid]" hide-details dense />
          </template>
          <template v-slot:[`item.name`]="{ item }">
            {{ item.first_name }} {{ item.last_name }}
          </template>
          <template v-slot:[`item.plate`]="{ item }">
            {{ item.active_vehicle_plate || '—' }}
          </template>
          <template v-slot:[`item.uuid`]="{ item }">
            <code class="caption">{{ item.driver_uuid.slice(0, 12) }}...</code>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon x-small
                  color="grey lighten-1"
                  :loading="ignoring[item.driver_uuid]"
                  v-bind="attrs" v-on="on"
                  @click="ignoreDriver(item)"
                >
                  <v-icon small>mdi-eye-off</v-icon>
                </v-btn>
              </template>
              <span>Ignoruj — nie pokazuj więcej tego kierowcy w synchronizacji</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card>

      <v-card v-if="!hasAnyChanges" outlined style="background: var(--bg-surface);">
        <v-card-text class="text-center grey--text">
          ✓ Wszystko już zsynchronizowane. Nic do zmiany.
        </v-card-text>
      </v-card>

      <!-- Apply button -->
      <v-card v-if="hasAnyChanges" outlined style="background: var(--bg-surface);">
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" :loading="applying" :disabled="!hasSelections" @click="applyChanges" large>
            <v-icon left>mdi-check-all</v-icon>
            {{ applying ? $t('boltSync.applying') : $t('boltSync.applyChanges') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>
    </v-tab-item>

    <!-- ====== Tab 2: Pojazdy ====== -->
    <v-tab-item v-if="canAccess('vehicles')">
      <v-alert v-if="vehError" type="error" outlined class="mb-3">{{ vehError }}</v-alert>

      <v-alert v-if="vehLoading" type="info" outlined dense class="mb-3">
        <div class="d-flex align-center">
          <v-progress-circular indeterminate size="18" width="2" class="mr-3" />
          <span>Pobieram listę pojazdów z API Bolta…</span>
        </div>
      </v-alert>

      <v-alert
        v-if="vehLastResult"
        :type="vehLastResult.assigned > 0 ? 'success' : 'info'"
        outlined dense class="mb-3"
      >
        Zatwierdzono: <strong>{{ vehLastResult.assigned }}</strong> aut.
        Już wcześniej zsynchronizowanych: {{ vehLastResult.alreadyAssigned }}.
        W Bolcie ale nie u nas: {{ vehLastResult.notFound }}.
        <v-btn icon x-small class="ml-2" @click="vehLastResult = null">
          <v-icon x-small>mdi-close</v-icon>
        </v-btn>
      </v-alert>

      <div v-if="vehPreview">
        <v-card outlined class="mb-4" style="background: var(--bg-surface);">
          <v-card-text class="d-flex flex-wrap" style="gap: 8px;">
            <v-chip color="info" outlined>Pojazdy w Bolcie: {{ vehPreview.totalBoltVehicles }}</v-chip>
            <v-chip color="primary" outlined>Auto-match: {{ vehPreview.autoAssign.length }}</v-chip>
            <v-chip color="success" outlined>Zsynchronizowane: {{ vehPreview.alreadyAssigned.length }}</v-chip>
            <v-chip color="grey" outlined v-if="vehPreview.ourOnly.length">Tylko u nas: {{ vehPreview.ourOnly.length }}</v-chip>
            <v-chip color="purple" outlined v-if="vehPreview.boltOnly.length">Tylko w Bolcie: {{ vehPreview.boltOnly.length }}</v-chip>
            <v-chip color="warning" outlined v-if="vehPreview.suspended.length">Zawieszone w Bolcie: {{ vehPreview.suspended.length }}</v-chip>
          </v-card-text>
        </v-card>

        <!-- AUTO-MATCH (do zatwierdzenia) -->
        <v-card outlined class="mb-4" v-if="vehPreview.autoAssign.length" style="background: var(--bg-surface);">
          <v-card-title class="subtitle-1">
            <v-icon left color="primary">mdi-check-circle-outline</v-icon>
            Do zatwierdzenia ({{ vehPreview.autoAssign.length }})
          </v-card-title>
          <v-card-subtitle class="grey--text">
            Auta dopasowane po tablicy rejestracyjnej. Zatwierdź żeby ustawić `boltVehicleId`.
          </v-card-subtitle>
          <v-card-text class="py-0">
            <v-btn x-small text @click="selectAllVehicles(true)">Zaznacz wszystkie</v-btn>
            <v-btn x-small text @click="selectAllVehicles(false)">Odznacz wszystkie</v-btn>
          </v-card-text>
          <v-data-table
            :items="vehPreview.autoAssign"
            :headers="vehMatchHeaders"
            :items-per-page="-1"
            hide-default-footer
            dark dense
          >
            <template v-slot:[`item.select`]="{ item }">
              <v-checkbox v-model="vehSelected[item.car.id]" hide-details dense />
            </template>
            <template v-slot:[`item.car`]="{ item }">
              <div class="font-weight-medium">{{ item.car.plate }}</div>
              <div class="caption grey--text">{{ item.car.make }} {{ item.car.model }}</div>
            </template>
            <template v-slot:[`item.bolt`]="{ item }">
              <div class="font-weight-medium">{{ item.bolt.reg_number }}</div>
              <div class="caption grey--text">
                {{ item.bolt.model }} ({{ item.bolt.year }})
                <code class="ml-1">#{{ item.bolt.id }}</code>
              </div>
            </template>
            <template v-slot:[`item.state`]="{ item }">
              <v-chip x-small :color="item.bolt.state === 'active' ? 'success' : 'warning'">
                {{ item.bolt.state }}
              </v-chip>
            </template>
          </v-data-table>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="primary"
              :loading="vehApplying"
              :disabled="!hasVehicleSelections"
              @click="applyVehicles"
              large
            >
              <v-icon left>mdi-check-all</v-icon>
              Zatwierdź zaznaczone
            </v-btn>
          </v-card-actions>
        </v-card>

        <!-- SUSPENDED (warning) -->
        <v-card
          v-if="vehPreview.suspended.length"
          outlined class="mb-4"
          style="background: var(--bg-surface); border-color: #FB8C00;"
        >
          <v-card-title class="subtitle-1 warning--text">
            <v-icon left color="warning">mdi-alert-outline</v-icon>
            Zawieszone / dezaktywowane w Bolcie ({{ vehPreview.suspended.length }})
          </v-card-title>
          <v-card-subtitle class="grey--text">
            Auta ze stanem `suspended` lub `deactivated`. Sprawdź u Bolta dlaczego.
          </v-card-subtitle>
          <v-data-table
            :items="vehPreview.suspended"
            :headers="vehSuspendedHeaders"
            :items-per-page="-1"
            hide-default-footer
            dark dense
          >
            <template v-slot:[`item.car`]="{ item }">
              <div v-if="item.car" class="font-weight-medium">{{ item.car.plate }}</div>
              <div v-else class="caption grey--text">(brak u nas w bazie)</div>
            </template>
            <template v-slot:[`item.bolt`]="{ item }">
              <div class="font-weight-medium">{{ item.bolt.reg_number }}</div>
              <div class="caption grey--text">{{ item.bolt.model }} ({{ item.bolt.year }})</div>
            </template>
            <template v-slot:[`item.state`]="{ item }">
              <v-chip x-small color="warning">{{ item.bolt.state }}</v-chip>
            </template>
            <template v-slot:[`item.reason`]="{ item }">
              <span class="caption">{{ item.bolt.suspension_reason || '—' }}</span>
            </template>
          </v-data-table>
        </v-card>

        <!-- BOLT-ONLY -->
        <v-card v-if="vehPreview.boltOnly.length" outlined class="mb-4" style="background: var(--bg-surface);">
          <v-card-title class="subtitle-1">
            <v-icon left color="purple">mdi-car-arrow-right</v-icon>
            Tylko w Bolcie ({{ vehPreview.boltOnly.length }})
            <v-spacer />
            <v-btn
              small color="primary"
              :loading="creatingFromBolt"
              @click="createAllVehiclesFromBolt()"
            >
              <v-icon left small>mdi-plus-circle</v-icon>
              Utwórz wszystkie w BTP ({{ vehPreview.boltOnly.length }})
            </v-btn>
          </v-card-title>
          <v-card-subtitle class="grey--text">
            Auta wystawione w Bolcie, ale nie ma ich w naszej flocie. Klik „Utwórz" — powstaną
            w „Flota" z automatycznie podpiętym Bolt ID. Pola jak rok, kolor, polisa
            uzupełnij ręcznie później w Flota.
          </v-card-subtitle>
          <v-data-table
            :items="vehPreview.boltOnly"
            :headers="vehBoltOnlyHeaders"
            :items-per-page="-1"
            hide-default-footer
            dark dense
          >
            <template v-slot:[`item.reg_number`]="{ item }">
              <strong>{{ item.reg_number }}</strong>
            </template>
            <template v-slot:[`item.state`]="{ item }">
              <v-chip x-small :color="item.state === 'active' ? 'success' : 'warning'">{{ item.state }}</v-chip>
            </template>
            <template v-slot:[`item.id`]="{ item }">
              <code class="caption">#{{ item.id }}</code>
            </template>
          </v-data-table>
        </v-card>

        <!-- ALREADY ASSIGNED (collapsible) -->
        <v-card v-if="vehPreview.alreadyAssigned.length" outlined class="mb-4" style="background: var(--bg-surface);">
          <v-expansion-panels accordion flat>
            <v-expansion-panel>
              <v-expansion-panel-header class="py-2">
                <div class="d-flex align-center">
                  <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                  <strong>Już zsynchronizowane ({{ vehPreview.alreadyAssigned.length }})</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-data-table
                  :items="vehPreview.alreadyAssigned"
                  :headers="vehAlreadyHeaders"
                  :items-per-page="-1"
                  hide-default-footer
                  dark dense
                >
                  <template v-slot:[`item.car`]="{ item }">
                    <strong>{{ item.car.plate }}</strong>
                    <div class="caption grey--text">{{ item.car.make }} {{ item.car.model }}</div>
                  </template>
                  <template v-slot:[`item.bolt`]="{ item }">
                    {{ item.bolt.model }} ({{ item.bolt.year }})
                    <code class="caption ml-1">#{{ item.bolt.id }}</code>
                  </template>
                </v-data-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>

        <v-card
          v-if="!vehPreview.autoAssign.length && !vehPreview.suspended.length && !vehPreview.boltOnly.length"
          outlined style="background: var(--bg-surface);"
        >
          <v-card-text class="text-center grey--text">
            ✓ Wszystkie pojazdy zsynchronizowane.
          </v-card-text>
        </v-card>
      </div>

      <div v-else-if="!vehLoading" class="text-center pa-8 grey--text">
        Kliknij „Odśwież listę pojazdów" w prawym górnym rogu żeby pobrać dane z Bolta.
      </div>
    </v-tab-item>
    </v-tabs-items>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="6000" top>
      {{ snackbar.text }}
    </v-snackbar>

    <!-- Dialog: lista zignorowanych kierowców -->
    <v-dialog v-model="showIgnoredDialog" max-width="800" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left>mdi-eye-off</v-icon>
          Zignorowani kierowcy Bolt ({{ ignoredList.length }})
          <v-spacer />
          <v-btn icon @click="showIgnoredDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-subtitle class="grey--text">
          Nie pojawiają się w sekcji „Do utworzenia". Klik „Cofnij" przywróci kierowcę przy następnej synchronizacji.
        </v-card-subtitle>
        <v-divider />
        <v-card-text style="max-height: 60vh;">
          <div v-if="ignoredLoading" class="text-center pa-6">
            <v-progress-circular indeterminate size="28" />
          </div>
          <v-alert v-else-if="ignoredList.length === 0" type="info" outlined dense>
            Brak zignorowanych kierowców.
          </v-alert>
          <v-data-table
            v-else
            :items="ignoredList"
            :headers="ignoredHeaders"
            :items-per-page="-1"
            hide-default-footer
            dense
          >
            <template v-slot:[`item.name`]="{ item }">
              {{ item.firstName || '—' }} {{ item.lastName || '' }}
            </template>
            <template v-slot:[`item.uuid`]="{ item }">
              <code class="caption">{{ item.boltUuid.slice(0, 12) }}…</code>
            </template>
            <template v-slot:[`item.createdAt`]="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                x-small
                color="primary"
                :loading="unignoring[item.boltUuid]"
                @click="unignoreDriver(item)"
              >
                <v-icon left x-small>mdi-undo</v-icon>
                Cofnij
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { canAccessSubtab } from "@/services/permissions";
const axios = require('axios');

export default {
  name: 'BoltSyncDriversPage',
  data: () => ({
    activeTab: 0,
    preview: null,
    loading: false,
    applying: false,
    jobMessage: '',
    error: null,
    lastErrors: [],     // szczegółowa lista błędów z ostatniego apply (do wyświetlenia w UI)
    lastResult: null,
    // Status centralnego BoltFetcher (cron co godzinę).
    fetcherStatus: null,
    fetcherRunning: false,
    selectedAuto: {},
    selectedSuggestions: {},
    selectedConflicts: {},
    selectedCreate: {},
    defaultCoopType: 'RENT',
    snackbar: { show: false, color: 'success', text: '' },
    // === Ignorowani kierowcy ===
    showIgnoredDialog: false,
    ignoredList: [],
    ignoredLoading: false,
    ignoring: {}, // { boltUuid: true } podczas zapisu
    unignoring: {},
    // === Pojazdy (tab 2) ===
    vehPreview: null,
    vehLoading: false,
    vehApplying: false,
    vehError: null,
    vehLastResult: null,
    vehSelected: {},
    creatingFromBolt: false,
  }),
  computed: {
    matchHeaders() {
      return [
        { text: '✓', value: 'select', sortable: false, width: 40 },
        { text: this.$t('boltSync.ourDriver'), value: 'ourDriver', sortable: false },
        { text: this.$t('boltSync.boltDriver'), value: 'boltDriver', sortable: false },
        { text: this.$t('boltSync.score'), value: 'score', width: 80 },
        { text: this.$t('boltSync.reasons'), value: 'reasons', sortable: false },
      ];
    },
    unmatchedHeaders() {
      return [
        { text: 'Kierowca BTP', value: 'driver', sortable: false },
        { text: 'Powód', value: 'reason', width: 280 },
        { text: 'Akcja', value: 'action', width: 140, sortable: false },
      ];
    },
    // Lista TWOICH BTP-ków którzy nadal nie mają Bolt UUID, z powodem dlaczego.
    // Klasyfikujemy każdego po obecnym stanie preview: w której sekcji się znajduje.
    unmatchedSummary() {
      if (!this.preview) return [];
      const result = [];
      const seen = new Set();
      const push = (drv, reason, reasonKey) => {
        if (!drv || seen.has(drv.id)) return;
        seen.add(drv.id);
        result.push({
          id: drv.id,
          firstName: drv.firstName || '',
          lastName: drv.lastName || '',
          email: drv.email || '',
          phone: drv.phone || '',
          reason,
          reasonKey,
        });
      };
      // 1) Auto (są w sekcji "Auto-przypisz" — wystarczy zaznaczyć i kliknąć Zastosuj)
      for (const m of this.preview.autoAssign || []) {
        push(m.ourDriver, 'Auto-przypisanie czeka — zaznacz checkbox i Zastosuj', 'auto');
      }
      // 2) Sugerowane (średnia pewność, wymaga decyzji)
      for (const m of this.preview.suggestions || []) {
        push(m.ourDriver, `Sugerowane dopasowanie (score ${m.score}) — wymaga akceptacji`, 'suggestion');
      }
      // 3) Konflikt ID (ma już INNE bolt_uuid, decyzja: podmienić czy zostawić)
      for (const m of this.preview.idConflicts || []) {
        push(m.ourDriver, 'Konflikt: kierowca ma już inny Bolt UUID — decyzja', 'conflict');
      }
      // 4) Bez dopasowania w Bolcie — nie ma w API Bolta nic co by się zgadzało
      for (const d of this.preview.ourUnmatched || []) {
        push(d, 'Brak dopasowania w API Bolta — nic do zrobienia tu (sprawdź czy nie jeździ, czy ma poprawny email/telefon)', 'no-bolt-match');
      }
      return result;
    },
    createHeaders() {
      return [
        { text: '✓', value: 'select', sortable: false, width: 40 },
        { text: 'Imię i nazwisko', value: 'name' },
        { text: 'Email', value: 'email' },
        { text: 'Telefon', value: 'phone' },
        { text: 'Pojazd', value: 'plate' },
        { text: 'UUID', value: 'uuid' },
        { text: 'Akcje', value: 'actions', sortable: false, width: 80 },
      ];
    },
    ignoredHeaders() {
      return [
        { text: 'Imię i nazwisko', value: 'name' },
        { text: 'Email', value: 'email' },
        { text: 'Telefon', value: 'phone' },
        { text: 'Pojazd', value: 'plate' },
        { text: 'UUID', value: 'uuid' },
        { text: 'Zignorowany', value: 'createdAt' },
        { text: '', value: 'actions', sortable: false, width: 110 },
      ];
    },
    cooperationTypeOptions() {
      return [
        { text: 'B2B VAT', value: 'B2B_VAT' },
        { text: 'B2B bez VAT', value: 'B2B_NO_VAT' },
        { text: '50/50', value: 'FIFTY_FIFTY' },
        { text: 'Najem', value: 'RENT' },
        { text: 'Połączenie', value: 'CONNECTION' },
        { text: 'Student', value: 'STUDENT' },
      ];
    },
    hasAnyChanges() {
      if (!this.preview) return false;
      return (
        this.preview.autoAssign.length > 0 ||
        this.preview.suggestions.length > 0 ||
        this.preview.idConflicts.length > 0 ||
        this.preview.toCreate.length > 0
      );
    },
    // Świeżość danych — kolor zależny od czasu od ostatniej synchronizacji.
    fetcherAgeMinutes() {
      if (!this.fetcherStatus?.lastDone?.finishedAt) return null;
      const ageMs = Date.now() - new Date(this.fetcherStatus.lastDone.finishedAt).getTime();
      return Math.floor(ageMs / 60000);
    },
    fetcherFreshnessType() {
      if (this.fetcherStatus?.running) return 'info';
      if (this.fetcherAgeMinutes === null) return 'warning';
      if (this.fetcherAgeMinutes < 90) return 'success';
      if (this.fetcherAgeMinutes < 360) return 'info';
      return 'warning';
    },
    fetcherFreshnessColor() {
      const t = this.fetcherFreshnessType;
      return t === 'success' ? 'success' : t === 'warning' ? 'warning' : 'info';
    },
    fetcherFreshnessIcon() {
      if (this.fetcherStatus?.running) return 'mdi-cloud-sync';
      if (this.fetcherFreshnessType === 'success') return 'mdi-check-circle';
      return 'mdi-clock-alert-outline';
    },
    fetcherFreshnessLabel() {
      if (this.fetcherStatus?.running) return 'Synchronizacja w toku…';
      if (this.fetcherAgeMinutes === null) return 'Brak synchronizacji';
      if (this.fetcherAgeMinutes < 90) return 'Dane aktualne';
      if (this.fetcherAgeMinutes < 360) return 'Dane sprzed kilku godzin';
      return 'Dane przestarzałe — uruchom synchronizację';
    },
    // Grupowanie błędów — kategoryzuje po typowych przyczynach żeby admin
    // od razu widział czy wymagana jest cleanup-data czy zmiana flow.
    errorGroups() {
      const groups = {
        noEmail: { label: 'Brak emaila — Bolt nie zwrócił adresu', items: [] },
        emailCollision: { label: 'Email już używany przez innego kierowcę', items: [] },
        phoneNameMismatch: { label: 'Numer telefonu się duplikuje, ale imiona różne', items: [] },
        other: { label: 'Inne błędy', items: [] },
      };
      for (const e of (this.lastErrors || [])) {
        const r = (e.reason || '').toLowerCase();
        if (r.includes('brak adresu email')) groups.noEmail.items.push(e);
        else if (r.includes('email') && r.includes('already exists')) groups.emailCollision.items.push(e);
        else if (r.includes('ręczna weryfikacja') || r.includes('numer')) groups.phoneNameMismatch.items.push(e);
        else groups.other.items.push(e);
      }
      return Object.entries(groups)
        .filter(([, g]) => g.items.length > 0)
        .map(([key, g]) => ({ key, label: g.label, items: g.items, count: g.items.length }));
    },
    hasSelections() {
      return (
        Object.values(this.selectedAuto).some(Boolean) ||
        Object.values(this.selectedSuggestions).some(Boolean) ||
        Object.values(this.selectedConflicts).some(Boolean) ||
        Object.values(this.selectedCreate).some(Boolean)
      );
    },
    // ===== Pojazdy =====
    vehMatchHeaders() {
      return [
        { text: '✓', value: 'select', sortable: false, width: 40 },
        { text: 'Auto u nas', value: 'car', sortable: false },
        { text: 'Pojazd w Bolcie', value: 'bolt', sortable: false },
        { text: 'Stan', value: 'state', width: 100 },
      ];
    },
    vehSuspendedHeaders() {
      return [
        { text: 'Auto u nas', value: 'car', sortable: false },
        { text: 'Pojazd w Bolcie', value: 'bolt', sortable: false },
        { text: 'Stan', value: 'state', width: 100 },
        { text: 'Powód', value: 'reason', sortable: false },
      ];
    },
    vehBoltOnlyHeaders() {
      return [
        { text: 'Tablica', value: 'reg_number' },
        { text: 'Model', value: 'model' },
        { text: 'Rok', value: 'year', width: 70 },
        { text: 'Stan', value: 'state', width: 100 },
        { text: 'ID Bolt', value: 'id', width: 90 },
      ];
    },
    vehAlreadyHeaders() {
      return [
        { text: 'Auto u nas', value: 'car', sortable: false },
        { text: 'Pojazd w Bolcie', value: 'bolt', sortable: false },
      ];
    },
    hasVehicleSelections() {
      return Object.values(this.vehSelected).some(Boolean);
    },
  },
  watch: {
    // Lazy-load vehicles preview when user pierwszy raz wchodzi na zakładkę Pojazdy.
    activeTab(v) {
      if (v === 1 && !this.vehPreview && !this.vehLoading) {
        this.loadVehiclesPreview();
      }
    },
    // Przy otwarciu dialogu "Zignorowani" — zaciągnij aktualną listę z API
    showIgnoredDialog(open) {
      if (open) this.loadIgnoredList();
    },
  },
  async mounted() {
    // Wczytaj status fetchera w tle (nie blokuj resumeOrLoad)
    this.loadFetcherStatus();
    await this.resumeOrLoad();
    // Refresh status fetchera co 30s żeby admin widział kiedy cron coś zassał — pomiń gdy karta w tle.
    this.fetcherStatusTimer = setInterval(() => { if (!document.hidden) this.loadFetcherStatus(); }, 30000);
  },
  beforeDestroy() {
    if (this.fetcherStatusTimer) clearInterval(this.fetcherStatusTimer);
  },
  methods: {
    canAccess(sub) { return canAccessSubtab('bolt-sync-drivers', sub); },
    authHeaders() { return { Authorization: 'Bearer ' + localStorage.getItem('token') }; },
    formatDate(d) {
      if (!d) return '—';
      try { return new Date(d).toLocaleString('pl-PL'); } catch { return d; }
    },
    async ignoreDriver(item) {
      const uuid = item.driver_uuid;
      this.$set(this.ignoring, uuid, true);
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'bolt/sync-drivers/ignore',
          headers: this.authHeaders(),
          data: { boltUuid: uuid },
        });
        // Usuń lokalnie z preview.toCreate (bez nowego GET-a)
        if (this.preview && Array.isArray(this.preview.toCreate)) {
          this.preview.toCreate = this.preview.toCreate.filter(
            (d) => d.driver_uuid !== uuid,
          );
        }
        this.preview.ignoredCount = (this.preview.ignoredCount || 0) + 1;
        // Wyczyść ew. zaznaczenie
        this.$delete(this.selectedCreate, uuid);
        this.snackbar = {
          show: true,
          color: 'success',
          text: `Zignorowano ${item.first_name || ''} ${item.last_name || ''}`,
        };
      } catch (e) {
        this.snackbar = {
          show: true,
          color: 'error',
          text: 'Nie udało się zignorować: ' + (e?.response?.data?.message || e.message),
        };
      } finally {
        this.$set(this.ignoring, uuid, false);
      }
    },
    async loadIgnoredList() {
      this.ignoredLoading = true;
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'bolt/sync-drivers/ignored',
          headers: this.authHeaders(),
        });
        this.ignoredList = res?.data?.data || [];
      } catch (e) {
        this.snackbar = { show: true, color: 'error', text: 'Błąd pobierania listy: ' + e.message };
      } finally {
        this.ignoredLoading = false;
      }
    },
    async unignoreDriver(item) {
      const uuid = item.boltUuid;
      this.$set(this.unignoring, uuid, true);
      try {
        await axios({
          method: 'delete',
          url: process.env.VUE_APP_ROOT_API + 'bolt/sync-drivers/ignore/' + encodeURIComponent(uuid),
          headers: this.authHeaders(),
        });
        this.ignoredList = this.ignoredList.filter((d) => d.boltUuid !== uuid);
        if (this.preview && this.preview.ignoredCount > 0) {
          this.preview.ignoredCount -= 1;
        }
        this.snackbar = {
          show: true,
          color: 'success',
          text: 'Cofnięto ignorowanie. Załaduj ponownie podgląd, żeby kierowca się pojawił.',
        };
      } catch (e) {
        this.snackbar = {
          show: true,
          color: 'error',
          text: 'Błąd: ' + (e?.response?.data?.message || e.message),
        };
      } finally {
        this.$set(this.unignoring, uuid, false);
      }
    },
    apiUrl(p) { return process.env.VUE_APP_ROOT_API + p; },

    reasonColor(key) {
      return ({
        auto: 'success',
        suggestion: 'warning',
        conflict: 'error',
        'no-bolt-match': 'grey',
      })[key] || 'grey';
    },
    // Scrolluje do odpowiedniej sekcji preview (auto / sugestie / konflikt).
    scrollToSection(key) {
      const titles = {
        auto: 'Auto',
        suggestion: 'Sugerowane',
        conflict: 'Konflikt',
      };
      const needle = titles[key];
      if (!needle) return;
      // Znajduje pierwszy v-card-title zawierający tę nazwę
      const cards = document.querySelectorAll('.v-card__title');
      for (const c of cards) {
        if (c.textContent && c.textContent.includes(needle)) {
          c.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }
    },

    // Hurtowe utworzenie aut z preview.boltOnly w naszej bazie BTP.
    // Każde dostaje od razu boltVehicleId zlinkowany.
    async createAllVehiclesFromBolt() {
      if (!this.vehPreview || !this.vehPreview.boltOnly.length) return;
      const count = this.vehPreview.boltOnly.length;
      const ok = await window.Swal?.fire?.({
        title: `Utworzyć ${count} aut w BTP?`,
        text: 'Każde dostanie placeholder dla roku, koloru, polisy — uzupełnisz później w Flota.',
        icon: 'question', showCancelButton: true,
        confirmButtonText: 'Tak, utwórz', cancelButtonText: 'Anuluj',
      });
      if (ok && !ok.isConfirmed) return;
      this.creatingFromBolt = true;
      try {
        const res = await axios({
          method: 'post',
          url: this.apiUrl('bolt/sync-vehicles/create-from-bolt'),
          headers: this.authHeaders(),
          data: {}, // wszystkie z boltOnly
        });
        const d = res.data?.data || {};
        const msg = `Utworzono: ${d.created || 0}. Pominięto: ${d.skipped || 0}.` +
          (d.errors?.length ? ` Błędy: ${d.errors.length}.` : '');
        if (window.Swal) await window.Swal.fire({ icon: 'success', title: 'Gotowe', text: msg });
        await this.loadVehiclesPreview();
      } catch (e) {
        const msg = e.response?.data?.message?.toString() || 'Nie udało się utworzyć aut';
        if (window.Swal) window.Swal.fire('Błąd', msg, 'error');
      } finally {
        this.creatingFromBolt = false;
      }
    },

    // ===== Pojazdy: preview + apply =====
    async loadVehiclesPreview() {
      this.vehLoading = true;
      this.vehError = null;
      try {
        const res = await axios({
          method: 'get',
          url: this.apiUrl('bolt/sync-vehicles/preview'),
          headers: this.authHeaders(),
        });
        this.vehPreview = res.data?.data || null;
        // Domyślnie zaznacz wszystkie auto-match (admin szybko Zatwierdzić).
        const sel = {};
        if (this.vehPreview && this.vehPreview.autoAssign) {
          for (const m of this.vehPreview.autoAssign) sel[m.car.id] = true;
        }
        this.vehSelected = sel;
      } catch (e) {
        this.vehError = e.response?.data?.message?.toString() || 'Nie udało się pobrać pojazdów';
        this.vehPreview = null;
      }
      this.vehLoading = false;
    },
    selectAllVehicles(value) {
      if (!this.vehPreview) return;
      const sel = {};
      for (const m of this.vehPreview.autoAssign) sel[m.car.id] = value;
      this.vehSelected = sel;
    },
    async applyVehicles() {
      if (!this.vehPreview) return;
      const carIds = Object.entries(this.vehSelected)
        .filter(([, v]) => v)
        .map(([k]) => k);
      if (carIds.length === 0) return;
      this.vehApplying = true;
      try {
        const res = await axios({
          method: 'post',
          url: this.apiUrl('bolt/sync-vehicles/apply'),
          headers: this.authHeaders(),
          data: { carIds },
        });
        this.vehLastResult = res.data?.data || null;
        this.snackbar = {
          show: true,
          color: 'success',
          text: `Zatwierdzono ${this.vehLastResult?.assigned || 0} pojazdów`,
        };
        await this.loadVehiclesPreview();
      } catch (e) {
        this.snackbar = {
          show: true,
          color: 'error',
          text: e.response?.data?.message?.toString() || 'Błąd zatwierdzania',
        };
      }
      this.vehApplying = false;
    },

    async loadFetcherStatus() {
      try {
        const res = await axios({
          method: 'get',
          url: this.apiUrl('bolt/fetcher/status'),
          headers: this.authHeaders(),
        });
        this.fetcherStatus = res.data || null;
      } catch (e) {
        // best-effort — nie pokazuj błędu, fetcher może nie być skonfigurowany
      }
    },
    async runFetcher() {
      this.fetcherRunning = true;
      try {
        const startRes = await axios({
          method: 'post',
          url: this.apiUrl('bolt/fetcher/run'),
          headers: this.authHeaders(),
        });
        const jobId = startRes.data?.jobId;
        if (!jobId) throw new Error('Brak jobId');
        await this.pollJob(jobId);
        await this.loadFetcherStatus();
        this.snackbar = { show: true, color: 'success', text: 'Synchronizacja zakończona — dane Bolta odświeżone' };
        // Po zakończeniu fetchera, automatycznie przeładuj preview
        await this.loadPreview();
      } catch (e) {
        this.snackbar = { show: true, color: 'error', text: e.response?.data?.message || e.message };
      }
      this.fetcherRunning = false;
    },
    formatRelative(iso) {
      if (!iso) return '—';
      const ms = Date.now() - new Date(iso).getTime();
      const m = Math.floor(ms / 60000);
      if (m < 1) return 'przed chwilą';
      if (m < 60) return `${m} min temu`;
      const h = Math.floor(m / 60);
      if (h < 24) return `${h}h ${m % 60}min temu`;
      const d = Math.floor(h / 24);
      return `${d} dni temu`;
    },
    /**
     * Po wejściu na stronę: pobierz ostatnie joby tego typu z backendu.
     * - Jeśli jest RUNNING → resume polling (admin wszedł podczas synchronizacji)
     * - Jeśli najnowszy DONE i jest w jego `result` preview → pokaż go (bez ponownego pull-a z API!)
     * - W przeciwnym razie nic nie rób — admin sam kliknie „Załaduj podgląd"
     */
    async resumeOrLoad() {
      try {
        const res = await axios({
          method: 'get',
          url: this.apiUrl('background-jobs?type=BOLT_SYNC_PREVIEW'),
          headers: this.authHeaders(),
        });
        const jobs = res.data || [];
        const running = jobs.find(j => j.status === 'RUNNING');
        if (running) {
          this.loading = true;
          this.jobMessage = (running.progress && running.progress.message) || 'Synchronizacja w tle…';
          try {
            const result = await this.pollJob(running.id);
            this.applyPreviewResult(result);
          } catch (e) {
            this.error = e.message;
          } finally {
            this.loading = false;
            this.jobMessage = '';
          }
          return;
        }
        const latestDone = jobs.find(j => j.status === 'DONE' && j.result);
        if (latestDone) {
          this.applyPreviewResult(latestDone.result);
          this.snackbar = {
            show: true,
            color: 'info',
            text: 'Pokazuję wynik z ' + new Date(latestDone.finishedAt || latestDone.updatedAt).toLocaleString('pl-PL') + '. Kliknij „Załaduj podgląd" by pobrać nowy.',
          };
          return;
        }
      } catch (e) {
        // Nie blokuj — pozwól adminowi kliknąć przycisk ręcznie
        // eslint-disable-next-line no-console
        console.warn('resumeOrLoad failed:', e.message);
      }
    },

    applyPreviewResult(result) {
      this.preview = result || null;
      if (this.preview) {
        this.selectedAuto = {};
        for (const m of this.preview.autoAssign) this.selectedAuto[m.ourDriver.id] = true;
        this.selectedSuggestions = {};
        this.selectedConflicts = {};
        this.selectedCreate = {};
      }
    },

    async loadPreview() {
      this.loading = true;
      this.error = null;
      this.jobMessage = '';
      try {
        // Async wariant — POST puszcza w tle, zwraca jobId.
        const startRes = await axios({
          method: 'post',
          url: this.apiUrl('bolt/sync-drivers/preview-async'),
          headers: this.authHeaders(),
        });
        const jobId = (startRes.data && startRes.data.data && startRes.data.data.id);
        if (!jobId) throw new Error('Brak jobId z backendu');
        const result = await this.pollJob(jobId);
        this.applyPreviewResult(result);
        this.snackbar = {
          show: true,
          color: 'success',
          text: `Synchronizacja zakończona — ${(result?.autoAssign || []).length} auto-dopasowań, ${(result?.suggestions || []).length} sugestii.`,
        };
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || e.message;
      } finally {
        this.loading = false;
        this.jobMessage = '';
      }
    },

    // Generic polling — co 2.5s pyta backend o status joba.
    // Zwraca `result` po DONE, rzuca błąd po FAILED, czeka aż zniknie z RUNNING.
    async pollJob(jobId) {
      const start = Date.now();
      const timeoutMs = 30 * 60 * 1000; // 30 min hard cap
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() - start > timeoutMs) throw new Error('Job timeout');
        const res = await axios({
          method: 'get',
          url: this.apiUrl('background-jobs/' + jobId),
          headers: this.authHeaders(),
        });
        const job = res.data;
        if (job.progress && job.progress.message) this.jobMessage = job.progress.message;
        if (job.status === 'DONE') return job.result;
        if (job.status === 'FAILED') throw new Error(job.error || 'Job failed');
        await new Promise((r) => setTimeout(r, 2500));
      }
    },
    selectAll(section, value) {
      if (section === 'auto') {
        for (const m of this.preview.autoAssign) this.$set(this.selectedAuto, m.ourDriver.id, value);
      } else if (section === 'create') {
        for (const m of this.preview.toCreate) this.$set(this.selectedCreate, m.driver_uuid, value);
      }
    },
    async applyChanges() {
      this.applying = true;
      try {
        const autoIds = Object.entries(this.selectedAuto).filter(([, v]) => v).map(([k]) => k);
        const suggestionAccepts = Object.entries(this.selectedSuggestions)
          .filter(([, v]) => v)
          .map(([driverId]) => {
            const m = this.preview.suggestions.find((s) => s.ourDriver.id === driverId);
            return { driverId, boltId: m?.boltDriver.driver_uuid };
          })
          .filter((x) => x.boltId);
        const conflictIds = Object.entries(this.selectedConflicts).filter(([, v]) => v).map(([k]) => k);
        const createUuids = Object.entries(this.selectedCreate).filter(([, v]) => v).map(([k]) => k);

        // Async wariant: POST puszcza apply w tle, dostajemy jobId, czekamy na result.
        const startRes = await axios({
          method: 'post',
          url: this.apiUrl('bolt/sync-drivers/apply-async'),
          headers: this.authHeaders(),
          data: {
            autoAssignIds: autoIds,
            suggestionAccepts,
            idConflictReplace: conflictIds,
            toCreateUuids: createUuids,
            defaultCooperationType: this.defaultCoopType,
          },
        });
        const jobId = (startRes.data && startRes.data.data && startRes.data.data.id);
        if (!jobId) throw new Error('Brak jobId');
        const d = (await this.pollJob(jobId)) || {};
        const baseText = this.$t('boltSync.applyResult', {
          assigned: d.assigned, conflicts: d.conflictsResolved, created: d.created, errors: (d.errors || []).length,
        });
        const emailsText = d.emailsUpdated ? ` · Zaktualizowano ${d.emailsUpdated} e-maili z Bolta` : '';
        this.snackbar = {
          show: true,
          color: d.errors && d.errors.length ? 'warning' : 'success',
          text: baseText + emailsText,
        };
        // Wystaw listę błędów — admin musi je zobaczyć żeby działać.
        this.lastErrors = (d.errors && d.errors.length) ? d.errors : [];
        this.lastResult = d;
        await this.loadPreview();
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message;
        this.snackbar = { show: true, color: 'error', text: msg };
      } finally {
        this.applying = false;
      }
    },
  },
};
</script>

<style scoped>
.bolt-sync-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5478FF;
  font-weight: 600;
}
.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0 0;
  color: #FFFFFF;
}
.page-subtitle {
  margin-top: 6px;
  color: #94A0B8;
  font-size: 13px;
}
.bolt-sync-tabs {
  background: #232838 !important;
  border-radius: 12px 12px 0 0;
  border: 1px solid #2A3142;
  border-bottom: 1px solid #2A3142;
  padding: 0 8px;
}
.bolt-sync-tabs >>> .v-tab {
  color: #94A0B8 !important;
  font-weight: 600;
  letter-spacing: -0.01em;
  text-transform: none;
}
.bolt-sync-tabs >>> .v-tab--active {
  color: #5478FF !important;
}
.bolt-sync-tabs >>> .v-tabs-slider {
  background-color: #5478FF !important;
}
.bolt-sync-tab-count {
  margin-left: 6px;
  background: rgba(148, 160, 184, 0.18);
  color: #94A0B8;
  border-radius: 999px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 700;
  min-width: 22px;
  text-align: center;
}
.bolt-sync-tabs >>> .v-tab--active .bolt-sync-tab-count {
  background: rgba(84, 120, 255, 0.18);
  color: #5478FF;
}
.bolt-sync-tab-count--warn {
  background: rgba(255, 167, 38, 0.18) !important;
  color: #FFB74D !important;
}
.bolt-sync-page >>> .v-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-color: #2A3142 !important;
}
.bolt-sync-page >>> .v-card__title {
  color: #FFFFFF !important;
}
.bolt-sync-page >>> .v-data-table {
  background: transparent !important;
  color: #E6EAF2 !important;
}
.bolt-sync-page >>> .v-data-table thead tr th {
  background: #1F2433 !important;
  color: #94A0B8 !important;
  border-bottom: 1px solid #2A3142 !important;
}
.bolt-sync-page >>> .v-data-table tbody tr td {
  border-bottom: 1px solid #2A3142 !important;
  color: #E6EAF2 !important;
}
.bolt-sync-page >>> .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.bolt-sync-page >>> .v-text-field input,
.bolt-sync-page >>> .v-text-field textarea,
.bolt-sync-page >>> .v-select__selection {
  color: #E6EAF2 !important;
}
.bolt-sync-page >>> .v-label {
  color: #94A0B8 !important;
}
.bolt-sync-page >>> .grey--text {
  color: #94A0B8 !important;
}
</style>
